import { supabase } from "./supabaseClient";
import { useEffect, useMemo, useState, useRef } from "react";

// ==================== UTILITIES ====================
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().split("T")[0];
const getVehicleMotDate = (vehicle) => vehicle?.mot_expiry || vehicle?.mot || null;
const getVehicleTaxDate = (vehicle) => vehicle?.tax_expiry || vehicle?.tax || null;

const daysLeft = (date) => {
  if (!date) return null;
  const a = new Date(date);
  const b = new Date();
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((a - b) / 86400000);
};

const fmt = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const getDaysLeftText = (date) => {
  const days = daysLeft(date);
  if (days === null) return "";
  if (days < 0) return `(Expired ${Math.abs(days)} days ago)`;
  if (days === 0) return `(Due today)`;
  return `(${days} days left)`;
};

// ==================== 🗂️ FIELD MAP (FIX #3) ====================
const fieldMap = {
  MOT: "mot_expiry",
  Tax: "tax_expiry",
  Insurance: "ins",
  Tacho: "tacho",
  DQC: "dexp"
};

// ==================== ACTION ENGINE ====================
const getNextAction = (vehicle) => {
  if (!vehicle) return null;
  const items = [
    { type: "MOT", days: daysLeft(getVehicleMotDate(vehicle)), action: "Book MOT", field: "mot_expiry" },
    { type: "Tax", days: daysLeft(getVehicleTaxDate(vehicle)), action: "Renew Tax", field: "tax_expiry" },
    { type: "Insurance", days: daysLeft(vehicle.ins), action: "Renew Insurance", field: "ins" },
    { type: "Tacho", days: daysLeft(vehicle.tacho), action: "Calibrate Tacho", field: "tacho" }
  ];
  const urgent = items
    .filter(i => i.days !== null && i.days >= 0 && i.days <= 7)
    .sort((a, b) => a.days - b.days)[0];
  return urgent || null;
};

const getUrgentItems = (vehicles, drivers, defects) => {
  const items = [];
  
  vehicles.forEach(v => {
    const action = getNextAction(v);
    if (action) {
      items.push({
        id: `veh-${v.id}-${action.type}`,
        priority: action.days,
        type: "vehicle",
        message: `${v.reg}: ${action.action} (${action.days}d)`,
        actionLabel: action.action,
        vehicle: v,
        actionType: action.type
      });
    }
  });

  drivers.forEach(d => {
    const dqcDays = daysLeft(d.dexp);
    if (dqcDays !== null && dqcDays >= 0 && dqcDays <= 30) {
      items.push({
        id: `drv-${d.id}-dqc`,
        priority: dqcDays,
        type: "driver",
        message: `${d.name}: DQC expires in ${dqcDays}d`,
        actionLabel: "Renew DQC",
        driver: d,
        actionType: "DQC"
      });
    }
  });

  defects.filter(d => d.stat !== "resolved" && d.sev === "critical").forEach(d => {
    items.push({
      id: `def-${d.id}`,
      priority: -1,
      type: "defect",
      message: `🚨 ${d.reg}: ${d.desc}`,
      actionLabel: "Resolve Defect",
      defect: d,
      actionType: "defect"
    });
  });

  return items.sort((a, b) => a.priority - b.priority);
};

// ==================== UI HELPERS ====================
const pill = (type) => {
  const map = {
    ok: { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
    warn: { bg: "#fed7aa", color: "#92400e", border: "#fdba74" },
    bad: { bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
    info: { bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
    mid: { bg: "#e0e7ff", color: "#3730a3", border: "#c7d2fe" },
    neutral: { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
  };
  const c = map[type] || map.neutral;
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: c.bg, color: c.color, border: `1px solid ${c.border}`
  };
};

const actionButton = (tone = "warn") => {
  const tones = {
    critical: { bg: "#dc2626", hover: "#b91c1c", color: "#fff" },
    warn: { bg: "#f97316", hover: "#ea580c", color: "#fff" },
    primary: { bg: "#6c63ff", hover: "#5a52e0", color: "#fff" },
    neutral: { bg: "#f3f4f6", hover: "#e5e7eb", color: "#374151" }
  };
  const t = tones[tone] || tones.primary;
  return {
    padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer",
    background: t.bg, color: t.color, fontWeight: 600, fontSize: 13,
    transition: "background 0.2s"
  };
};

// ==================== REUSABLE COMPONENTS ====================
function StatusPill({ days }) {
  if (days === null) return <span style={pill("neutral")}>Not set</span>;
  if (days < 0) return <span style={pill("bad")}>Expired</span>;
  if (days <= 30) return <span style={pill("warn")}>{days}d left</span>;
  if (days <= 90) return <span style={pill("mid")}>{days}d left</span>;
  return <span style={pill("ok")}>Valid</span>;
}

function StatCard({ icon, label, value, sub, tone = "primary" }) {
  const toneMap = {
    primary: { bg: "#ede7ff", border: "#c4b5fd", value: "#5b21b6" },
    success: { bg: "#d1fae5", border: "#a7f3d0", value: "#065f46" },
    warn: { bg: "#ffedd5", border: "#fdba74", value: "#92400e" },
    danger: { bg: "#fee2e2", border: "#fecaca", value: "#991b1b" },
  };
  const t = toneMap[tone] || toneMap.primary;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 16, padding: 16 }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: t.value }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, size = "medium" }) {
  const sizes = { small: 500, medium: 700, large: 900 };
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} 
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", 
                  alignItems: "center", justifyContent: "center", padding: 18, zIndex: 1000 }}>
      <div style={{ width: "100%", maxWidth: sizes[size], maxHeight: "90vh", overflowY: "auto", 
                   background: "#fff", borderRadius: 20, boxShadow: "0 25px 80px rgba(0,0,0,0.4)", 
                   padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 28, cursor: "pointer", color: "#6b7280", lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function UrgentActionCard({ item, onActionClick }) {
  const isCritical = item.priority < 0 || item.priority <= 3;
  const tone = isCritical ? "critical" : "warn";
  
  return (
    <div style={{ 
      display: "flex", alignItems: "center", gap: 12, padding: 14, 
      background: isCritical ? "#fef2f2" : "#fff7ed", border: `1px solid ${isCritical ? "#fecaca" : "#fed7aa"}`, 
      borderRadius: 12, marginBottom: 10 
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: isCritical ? "#991b1b" : "#92400e" }}>{item.message}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
          {item.type === "vehicle" && `🚛 ${item.vehicle?.reg}`}
          {item.type === "driver" && `👤 ${item.driver?.name}`}
          {item.type === "defect" && `🔧 Defect #${item.defect?.id?.slice(0,6)}`}
        </div>
      </div>
      <button onClick={() => onActionClick(item)} style={actionButton(tone)}>
        {item.actionLabel}
      </button>
    </div>
  );
}

// ==================== 🎯 ACTION MODAL (FIXED) ====================
function ActionModal({ actionData, onClose, onConfirm }) {
  const [dueDate, setDueDate] = useState(today());
  const [notes, setNotes] = useState("");

  const handleConfirm = () => {
    if (!dueDate) { alert("Please select a due date"); return; }
    onConfirm({
      ...actionData,
      dueDate,
      notes,
      confirmedAt: new Date().toISOString()
    });
  };

  const vehicle = actionData?.vehicle;
  const fieldName = fieldMap[actionData?.actionType];
  const currentField = vehicle?.[fieldName];

  return (
    <Modal title={`✍️ ${actionData?.action}`} onClose={onClose} size="small">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Vehicle</div>
        <div style={{ fontWeight: 600, fontSize: 16 }}>{vehicle?.reg} — {vehicle?.make} {vehicle?.model}</div>
      </div>

      {currentField && (
        <div style={{ background: "#f8fafc", padding: 12, borderRadius: 10, marginBottom: 20, fontSize: 13 }}>
          <strong>Current {actionData?.actionType}:</strong> {fmt(currentField)} {getDaysLeftText(currentField)}
        </div>
      )}

      <Field label="Due Date *" required>
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} 
               style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} />
      </Field>

      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} 
                  style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8, minHeight: 80 }} 
                  placeholder="Add any additional details..." />
      </Field>

      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "10px 24px", background: "#f3f4f6", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 500 }}>Cancel</button>
        <button onClick={handleConfirm} style={{ padding: "10px 24px", background: "#6c63ff", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>✅ Confirm Task</button>
      </div>
    </Modal>
  );
}

// ==================== FORMS ====================
function VehicleForm({ data, onSave, onClose }) {
  const [f, setF] = useState(data || {
    reg: "",
    make: "",
    model: "",
    type: "Artic HGV",
    fleet: "",
    is_hgv: true,
    mot_expiry: "",
    tax_expiry: "",
    ins: "",
    tacho: "",
    pmi_interval_weeks: 6,
    last_pmi_date: "",
    depot: "",
    notes: ""
  });
  const upd = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        <Field label="Registration *" required><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.reg} onChange={upd("reg")} /></Field>
        <Field label="Make *" required><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.make} onChange={upd("make")} /></Field>
        <Field label="Model"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.model} onChange={upd("model")} /></Field>
        <Field label="Type"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.type} onChange={upd("type")}><option>Artic HGV</option><option>Rigid HGV</option><option>Van</option><option>Trailer</option></select></Field>
        <Field label="Fleet No"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.fleet} onChange={upd("fleet")} /></Field>
        <Field label="Depot"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.depot} onChange={upd("depot")} /></Field>
        <Field label="Vehicle Class">
          <select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={String(Boolean(f.is_hgv))} onChange={(e) => setF(p => ({ ...p, is_hgv: e.target.value === "true" }))}>
            <option value="true">HGV</option>
            <option value="false">Non-HGV</option>
          </select>
        </Field>
        <Field label="MOT Expiry *" required><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.mot_expiry || f.mot || ""} onChange={upd("mot_expiry")} /></Field>
        <Field label="Tax Expiry"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.tax_expiry || f.tax || ""} onChange={upd("tax_expiry")} /></Field>
        <Field label="Insurance Expiry"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.ins} onChange={upd("ins")} /></Field>
        <Field label="Tacho Calibration"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.tacho} onChange={upd("tacho")} /></Field>
        <Field label="PMI Interval (Weeks)"><input type="number" min="1" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.pmi_interval_weeks || ""} onChange={upd("pmi_interval_weeks")} /></Field>
        <Field label="Last PMI Date"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.last_pmi_date || ""} onChange={upd("last_pmi_date")} /></Field>
      </div>
      <Field label="Notes"><textarea style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8, minHeight: 80 }} value={f.notes} onChange={upd("notes")} /></Field>
      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
        <button onClick={() => { if (!f.reg || !f.make || !(f.mot_expiry || f.mot)) { alert("Fill required fields"); return; } onSave(f); }} style={{ padding: "10px 20px", background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Save</button>
      </div>
    </>
  );
}

function DriverForm({ data, onSave, onClose, vehicles }) {
  const [f, setF] = useState(data || { 
    name: "", lic: "", cat: "C+E", status: "active", phone: "", email: "", 
    dexp: "", digitalCardExpiry: "", cpc: 0, med: "", depot: "", veh: "", notes: "" 
  });
  const upd = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const selectedVehicle = vehicles.find(v => v.reg === f.veh);
  
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        <Field label="Full Name *" required><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.name} onChange={upd("name")} /></Field>
        <Field label="Licence Number *" required><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.lic} onChange={upd("lic")} /></Field>
        <Field label="Category"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.cat} onChange={upd("cat")}><option>C+E</option><option>C</option><option>C1</option></select></Field>
        <Field label="Status"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.status} onChange={upd("status")}><option>active</option><option>agency</option><option>suspended</option></select></Field>
        <Field label="Phone"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.phone} onChange={upd("phone")} /></Field>
        <Field label="Email"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.email} onChange={upd("email")} /></Field>
        <Field label="DQC Expiry"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.dexp} onChange={upd("dexp")} /></Field>
        <Field label="Digital Card Expiry"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.digitalCardExpiry} onChange={upd("digitalCardExpiry")} /></Field>
        <Field label="CPC Hours"><input type="number" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.cpc} onChange={upd("cpc")} /></Field>
        <Field label="Medical Due"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.med} onChange={upd("med")} /></Field>
        <Field label="Depot"><input style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.depot} onChange={upd("depot")} /></Field>
        <Field label="Assigned Vehicle">
          <select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.veh} onChange={upd("veh")}>
            <option value="">-- Select Vehicle --</option>
            {vehicles.map(v => <option key={v.id} value={v.reg}>{v.reg} - {v.make} {v.model}</option>)}
          </select>
          {selectedVehicle && (
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, background: "#f8fafc", padding: 8, borderRadius: 8 }}>
              <strong>Assigned:</strong> {selectedVehicle.reg} | MOT: {fmt(getVehicleMotDate(selectedVehicle))} {getDaysLeftText(getVehicleMotDate(selectedVehicle))}
            </div>
          )}
        </Field>
      </div>
      <Field label="Notes"><textarea style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8, minHeight: 80 }} value={f.notes} onChange={upd("notes")} /></Field>
      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
        <button onClick={() => { if (!f.name || !f.lic) { alert("Fill required fields"); return; } onSave(f); }} style={{ padding: "10px 20px", background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Save</button>
      </div>
    </>
  );
}

function DefectForm({ data, onSave, onClose, vehicles, drivers }) {
  const [f, setF] = useState(data || { reg: "", drv: "", date: today(), cat: "Tyres", sev: "minor", stat: "open", desc: "", rdate: "", rby: "", rnotes: "" });
  const upd = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        <Field label="Vehicle">
          <select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.reg} onChange={upd("reg")}>
            <option value="">-- Select Vehicle --</option>
            {vehicles.map(v => <option key={v.id} value={v.reg}>{v.reg} - {v.make}</option>)}
          </select>
        </Field>
        <Field label="Driver">
          <select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.drv} onChange={upd("drv")}>
            <option value="">-- Select Driver --</option>
            {drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Category"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.cat} onChange={upd("cat")}><option>Tyres</option><option>Lights</option><option>Brakes</option><option>Engine</option><option>Bodywork</option></select></Field>
        <Field label="Severity"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.sev} onChange={upd("sev")}><option>minor</option><option>major</option><option>critical</option></select></Field>
        <Field label="Status"><select style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.stat} onChange={upd("stat")}><option>open</option><option>in_progress</option><option>resolved</option></select></Field>
        <Field label="Date"><input type="date" style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8 }} value={f.date} onChange={upd("date")} /></Field>
      </div>
      <Field label="Description *" required><textarea style={{ width: "100%", padding: 10, border: "1px solid #d1d5db", borderRadius: 8, minHeight: 80 }} value={f.desc} onChange={upd("desc")} /></Field>
      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "10px 20px", background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
        <button onClick={() => { if (!f.reg || !f.desc) { alert("Fill required fields"); return; } onSave(f); }} style={{ padding: "10px 20px", background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Save</button>
      </div>
    </>
  );
}

// ==================== 🚀 MAIN COMPONENT ====================
export default function FleetManager() {
  // Core States
  const [view, setView] = useState("dashboard");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [defects, setDefects] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  
  // ✅ FIX #1: Single state for action modal
  const [actionModal, setActionModal] = useState(null);
  
  const [editData, setEditData] = useState(null);
  const [toast, setToast] = useState(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("viewer");
  const [organisationId, setOrganisationId] = useState(null);
  const [planType, setPlanType] = useState("free");
  const [planLimitTarget, setPlanLimitTarget] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [isLogin, setIsLogin] = useState(true);

  // ✅ FIX #1: Alert guard ref
  const alertedRef = useRef(new Set());

  // 🏆 Memoized Urgent Actions
  const topUrgentActions = useMemo(() => {
    return getUrgentItems(vehicles, drivers, defects).slice(0, 3);
  }, [vehicles, drivers, defects]);

  // Auth Effects
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      if (session?.user) {
        const userRole = session.user.user_metadata?.role || "viewer";
        setRole(userRole);
        loadAlerts(session.user.id);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        const userRole = session.user.user_metadata?.role || "viewer";
        setRole(userRole);
        loadAlerts(session.user.id);
      } else {
        setRole("viewer"); setAlerts([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => { if (user) { loadVehicles(); loadDrivers(); loadDefects(); } }, [user]);

  useEffect(() => {
    if (user?.id) loadOrganisationPlan(user.id);
    else {
      setOrganisationId(null);
      setPlanType("free");
    }
  }, [user]);

  // Data Loaders
  const loadVehicles = async () => {
    const { data, error } = await supabase.from("vehicles").select("*").eq("driver", user?.id);
    if (error) console.error(error); else setVehicles(data || []);
  };
  const loadOrganisationPlan = async (userId) => {
    const { data: member, error: memberError } = await supabase
      .from("organisation_members")
      .select("organisation_id")
      .eq("user_id", userId)
      .maybeSingle();

    if (memberError) {
      console.error("Failed to load organisation membership:", memberError);
      setOrganisationId(null);
      setPlanType("free");
      return;
    }

    const nextOrganisationId = member?.organisation_id || null;
    setOrganisationId(nextOrganisationId);

    if (!nextOrganisationId) {
      setPlanType("free");
      return;
    }

    const { data: organisation, error: organisationError } = await supabase
      .from("organisations")
      .select("plan_type")
      .eq("id", nextOrganisationId)
      .maybeSingle();

    if (organisationError) {
      console.error("Failed to load organisation plan:", organisationError);
      setPlanType("free");
      return;
    }

    setPlanType(organisation?.plan_type || "free");
  };
  const loadDrivers = () => {
    const stored = localStorage.getItem(`fms_d_${user?.id}`);
    setDrivers(stored ? JSON.parse(stored) : []);
  };
  const loadDefects = () => {
    const stored = localStorage.getItem(`fms_def_${user?.id}`);
    setDefects(stored ? JSON.parse(stored) : []);
  };
  const loadAlerts = async (userId) => {
    const { data, error } = await supabase.from("alerts").select("*").eq("user_id", userId).order("created_at", { ascending: false });
    if (!error) setAlerts(data || []);
  };

  // Persistence Helpers
  const saveDrivers = (newDrivers) => { localStorage.setItem(`fms_d_${user?.id}`, JSON.stringify(newDrivers)); setDrivers(newDrivers); };
  const saveDefects = (newDefects) => { localStorage.setItem(`fms_def_${user?.id}`, JSON.stringify(newDefects)); setDefects(newDefects); };

  // ✅ Action Handlers
  const handleActionClick = (item) => {
    setActionModal({
      action: item.actionLabel,
      vehicle: item.vehicle,
      driver: item.driver,
      defect: item.defect,
      actionType: item.actionType,
      priority: item.priority
    });
  };

  // ✅ FIX #2: Unified handler - receives single object
  const handleConfirmAction = (data) => {
    const newTask = {
      id: uid(),
      vehicleReg: data.vehicle?.reg || (data.driver ? `Driver: ${data.driver.name}` : "N/A"),
      action: data.action,
      dueDate: data.dueDate,
      notes: data.notes,
      status: "pending",
      createdAt: data.confirmedAt,
      source: data.vehicle ? "vehicle" : data.driver ? "driver" : "defect",
      actionType: data.actionType
    };
    setTasks(prev => [newTask, ...prev]);
    showToast(`✅ Task created: ${data.action}`, "ok");
    setActionModal(null);
  };

  // ✅ FIX #5: Task completion with feedback
  const completeTask = (taskId) => {
    setTasks(prev => prev.map(t => {
      if (t.id === taskId && t.status === "pending") {
        showToast("Task completed ✅", "ok");
        addAlert(`Task completed: ${t.action}`, "info");
        return { ...t, status: "completed", completedAt: new Date().toISOString() };
      }
      return t;
    }));
  };

  const addAlert = async (message, type) => {
    if (!user) return;
    const { error } = await supabase.from("alerts").insert([{ user_id: user.id, message, type, read: false }]);
    if (!error) loadAlerts(user.id);
  };

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // Auth Handlers
  const handleSignUp = async () => {
    if (!authEmail || !authPass) { alert("Enter email and password"); return; }
    const { error } = await supabase.auth.signUp({ email: authEmail, password: authPass, options: { data: { role: "admin" } } });
    if (error) alert(error.message);
    else { alert("Sign up successful! Please login."); setIsLogin(true); setAuthEmail(""); setAuthPass(""); }
  };
  const handleLogin = async () => {
    if (!authEmail || !authPass) { alert("Enter email and password"); return; }
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPass });
    if (error) alert(error.message);
    else { setAuthEmail(""); setAuthPass(""); showToast("Logged in"); }
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setRole("viewer"); setOrganisationId(null); setPlanType("free"); setVehicles([]); setDrivers([]); setDefects([]); setAlerts([]);
    showToast("Logged out");
  };

  const isAdmin = role === "admin";

  const openPlanLimitModal = (target) => {
    setPlanLimitTarget(target);
    setModal("planLimit");
  };

  const ensurePlanWithinLimit = (target, isEditing = false) => {
    if (isEditing || planType !== "free") return true;
    const currentCount = target === "vehicle" ? vehicles.length : drivers.length;
    if (currentCount >= 2) {
      openPlanLimitModal(target);
      return false;
    }
    return true;
  };

  // CRUD Handlers
  const saveVehicle = async (v) => {
    if (!user) return;
    if (!ensurePlanWithinLimit("vehicle", Boolean(v.id))) return;
    try {
      const motExpiry = v.mot_expiry || v.mot || null;
      const taxExpiry = v.tax_expiry || v.tax || null;
      const payload = {
        reg: v.reg?.trim()?.toUpperCase() || null,
        make: v.make?.trim() || null,
        model: v.model?.trim() || null,
        type: v.type?.trim() || null,
        fleet: v.fleet?.trim() || null,
        is_hgv: Boolean(v.is_hgv),
        mot_expiry: motExpiry,
        tax_expiry: taxExpiry,
        pmi_interval_weeks: v.pmi_interval_weeks ? parseInt(v.pmi_interval_weeks, 10) : null,
        last_pmi_date: v.last_pmi_date || null,
        ins: v.ins || null,
        tacho: v.tacho || null,
        driver: user.id,
        organisation_id: organisationId,
        depot: v.depot?.trim() || null,
        notes: v.notes?.trim() || null
      };
      if (v.id) { const { error } = await supabase.from("vehicles").update(payload).eq("id", v.id); if (error) throw error; showToast("Vehicle updated"); }
      else { const { error } = await supabase.from("vehicles").insert([payload]); if (error) throw error; showToast("Vehicle added"); await addAlert(`New vehicle added: ${v.reg}`, "info"); }
      await loadVehicles(); closeModal();
    } catch (err) { alert("Error: " + err.message); }
  };
  const delVehicle = async (id, reg) => {
    if (!isAdmin) { alert("Only admin can delete"); return; }
    if (!window.confirm("Delete vehicle?")) return;
    const { error } = await supabase.from("vehicles").delete().eq("id", id);
    if (error) alert("Error: " + error.message);
    else { showToast("Vehicle deleted", "warn"); await addAlert(`Vehicle deleted: ${reg}`, "warn"); await loadVehicles(); }
  };
  const saveDriver = (d) => {
    if (!isAdmin && !d.id) { alert("Only admin can add drivers"); return; }
    if (!ensurePlanWithinLimit("driver", Boolean(d.id))) return;
    let newDrivers = d.id ? drivers.map(x => x.id === d.id ? d : x) : [...drivers, { ...d, id: uid() }];
    showToast(d.id ? "Driver updated" : "Driver added");
    if (!d.id) addAlert(`New driver added: ${d.name}`, "info");
    saveDrivers(newDrivers); closeModal();
  };
  const delDriver = (id, name) => {
    if (!isAdmin) { alert("Only admin can delete"); return; }
    if (!window.confirm("Delete driver?")) return;
    saveDrivers(drivers.filter(x => x.id !== id));
    showToast("Driver deleted", "warn"); addAlert(`Driver deleted: ${name}`, "warn");
  };
  const saveDefect = (d) => {
    let newDefects = d.id ? defects.map(x => x.id === d.id ? d : x) : [...defects, { ...d, id: uid() }];
    showToast(d.id ? "Defect updated" : "Defect logged");
    if (!d.id && d.sev === "critical") addAlert(`Critical defect on ${d.reg}: ${d.desc}`, "critical");
    saveDefects(newDefects); closeModal();
  };
  const delDefect = (id) => {
    if (!isAdmin) { alert("Only admin can delete"); return; }
    if (!window.confirm("Delete defect?")) return;
    saveDefects(defects.filter(x => x.id !== id));
    showToast("Defect deleted", "warn");
  };

  const closeModal = () => { setModal(null); setEditData(null); setPlanLimitTarget(""); };
  const openModal = (type, data = null) => {
    if (!data && (type === "vehicle" || type === "driver") && !ensurePlanWithinLimit(type)) return;
    setModal(type);
    setEditData(data);
  };

  // Status Helpers
  const vehicleStatus = (v) => {
    const arr = [getVehicleMotDate(v), getVehicleTaxDate(v), v.ins, v.tacho].filter(Boolean).map(daysLeft);
    if (arr.some(d => d !== null && d < 0)) return "bad";
    if (arr.some(d => d !== null && d <= 30)) return "warn";
    return "ok";
  };
  const driverStatus = (d) => {
    const dqc = daysLeft(d.dexp);
    if (dqc !== null && dqc < 0) return "bad";
    if (dqc !== null && dqc <= 90) return "warn";
    return "ok";
  };

  // Filters
  const openDefects = defects.filter(d => d.stat !== "resolved").length;
  const unreadAlerts = alerts.filter(a => !a.read).length;
  const expiring30 = useMemo(() => {
    let count = 0;
    vehicles.forEach(v => [getVehicleMotDate(v), getVehicleTaxDate(v), v.ins, v.tacho].forEach(value => { const d = daysLeft(value); if (d !== null && d >= 0 && d <= 30) count++; }));
    drivers.forEach(d => { const dqc = daysLeft(d.dexp); if (dqc !== null && dqc >= 0 && dqc <= 30) count++; });
    return count;
  }, [vehicles, drivers]);

  const filteredVehicles = vehicles.filter(v => {
    const hit = `${v.reg} ${v.make} ${v.model || ""}`.toLowerCase().includes(search.toLowerCase());
    if (!hit) return false;
    if (!filter) return true;
    return vehicleStatus(v) === filter;
  });
  const filteredDrivers = drivers.filter(d => {
    const hit = `${d.name} ${d.lic}`.toLowerCase().includes(search.toLowerCase());
    if (!hit) return false;
    if (!filter) return true;
    if (filter === "active") return d.status === "active";
    if (filter === "expiring") { const dy = daysLeft(d.dexp); return dy !== null && dy >= 0 && dy <= 90; }
    return true;
  });
  const filteredDefects = defects.filter(d => {
    const hit = `${d.reg} ${d.drv} ${d.desc}`.toLowerCase().includes(search.toLowerCase());
    if (!hit) return false;
    if (!filter) return true;
    return d.stat === filter;
  });

  // ✅ FIX #1: Auto Alert Engine - TOP LEVEL HOOK
  useEffect(() => {
    if (!user) return;

    vehicles.forEach(v => {
      const action = getNextAction(v);
      if (action && action.days <= 3) {
        const key = `${v.id}-${action.type}`;
        if (!alertedRef.current.has(key)) {
          alertedRef.current.add(key);
          addAlert(`🚨 ${v.reg}: ${action.action} due in ${action.days} days`, "critical");
        }
      }
    });

    drivers.forEach(d => {
      const dqcDays = daysLeft(d.dexp);
      if (dqcDays !== null && dqcDays >= 0 && dqcDays <= 7) {
        const key = `drv-${d.id}-dqc`;
        if (!alertedRef.current.has(key)) {
          alertedRef.current.add(key);
          addAlert(`⚠️ ${d.name}: DQC expires in ${dqcDays} days`, "warn");
        }
      }
    });

  }, [vehicles, drivers, user]);

  // ✅ FIX #4: Task Reminder Engine - TOP LEVEL HOOK
  useEffect(() => {
    tasks.forEach(t => {
      if (t.status !== "pending") return;
      const d = daysLeft(t.dueDate);
      if (d === 1) {
        showToast(`⏰ Reminder: ${t.action} due tomorrow`, "warn");
      }
    });
  }, [tasks]);

  // 🛑 Login Screen
  if (!user) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: 24, padding: 40, width: 420, maxWidth: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🚛</div>
          <h1 style={{ marginBottom: 8, fontSize: 28, color: "#1f2937" }}>Fleet Manager</h1>
          <p style={{ marginBottom: 32, color: "#6b7280", fontSize: 14 }}>Compliance Decision System</p>
          <input type="email" placeholder="Email address" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} style={{ width: "100%", padding: 12, marginBottom: 12, border: "1px solid #d1d5db", borderRadius: 10, boxSizing: "border-box", fontSize: 14 }} />
          <input type="password" placeholder="Password" value={authPass} onChange={(e) => setAuthPass(e.target.value)} style={{ width: "100%", padding: 12, marginBottom: 20, border: "1px solid #d1d5db", borderRadius: 10, boxSizing: "border-box", fontSize: 14 }} />
          {isLogin ? (
            <>
              <button onClick={handleLogin} style={{ width: "100%", padding: 12, background: "#6c63ff", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Login</button>
              <button onClick={() => setIsLogin(false)} style={{ width: "100%", padding: 12, background: "#fff", border: "1px solid #6c63ff", color: "#6c63ff", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>Create Account</button>
            </>
          ) : (
            <>
              <button onClick={handleSignUp} style={{ width: "100%", padding: 12, background: "#6c63ff", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Sign Up</button>
              <button onClick={() => setIsLogin(true)} style={{ width: "100%", padding: 12, background: "#fff", border: "1px solid #6c63ff", color: "#6c63ff", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>Back to Login</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // 🚀 Main App UI
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f3f4f6" }}>
      {/* Sidebar */}
      <aside style={{ width: 260, background: "#1f2937", padding: 20, color: "#fff", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 30 }}>
          <span style={{ fontSize: 32 }}>🚛</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>Fleet Manager</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{user?.email}</div>
            <div style={{ fontSize: 11, color: "#6c63ff", marginTop: 4 }}>Role: {role}</div>
          </div>
        </div>
        <nav style={{ flex: 1 }}>
          {[
            { id: "dashboard", label: "Dashboard", icon: "📊" },
            { id: "vehicles", label: "Vehicles", icon: "🚛", count: vehicles.length },
            { id: "drivers", label: "Drivers", icon: "👤", count: drivers.length },
            { id: "defects", label: "Defects", icon: "🔧", count: openDefects },
            { id: "tasks", label: "My Tasks", icon: "✅", count: tasks.filter(t => t.status === "pending").length },
            { id: "alerts", label: "Alerts", icon: "🔔", count: unreadAlerts },
          ].map(item => (
            <button key={item.id} onClick={() => { setView(item.id); setSearch(""); setFilter(""); }}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 16px", marginBottom: 8,
                background: view === item.id ? "#6c63ff" : "transparent", border: "none", borderRadius: 12,
                color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 500 }}>
              <span>{item.icon}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
              {item.count > 0 && <span style={{ background: "#374151", padding: "2px 8px", borderRadius: 20, fontSize: 11 }}>{item.count}</span>}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} style={{ width: "100%", padding: "12px 16px", background: "#dc2626", border: "none", borderRadius: 12, color: "#fff", cursor: "pointer", fontWeight: 500 }}>Logout</button>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, margin: 0, textTransform: "capitalize" }}>{view}</h1>
          {!isAdmin && view !== "alerts" && view !== "tasks" && <span style={{ background: "#fef3c7", padding: "6px 12px", borderRadius: 20, fontSize: 12, color: "#92400e" }}>View Only</span>}
          {view === "vehicles" && isAdmin && <button onClick={() => openModal("vehicle")} style={actionButton("primary")}>+ Add Vehicle</button>}
          {view === "drivers" && isAdmin && <button onClick={() => openModal("driver")} style={actionButton("primary")}>+ Add Driver</button>}
          {view === "defects" && isAdmin && <button onClick={() => openModal("defect")} style={actionButton("primary")}>+ Log Defect</button>}
        </div>

        {/* Search & Filter */}
        {view !== "alerts" && view !== "tasks" && (
          <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
            <input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, padding: "10px 16px", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14 }} />
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: "10px 16px", border: "1px solid #d1d5db", borderRadius: 10, background: "#fff" }}>
              <option value="">All</option>
              {view === "vehicles" && <><option value="ok">Compliant</option><option value="warn">Action Needed</option><option value="bad">Non-Compliant</option></>}
              {view === "drivers" && <><option value="active">Active</option><option value="expiring">Expiring Soon</option></>}
              {view === "defects" && <><option value="open">Open</option><option value="resolved">Resolved</option></>}
            </select>
          </div>
        )}

        {/* 🏆 DASHBOARD */}
        {view === "dashboard" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
              <StatCard icon="🚛" label="Total Vehicles" value={vehicles.length} />
              <StatCard icon="👤" label="Total Drivers" value={drivers.length} />
              <StatCard icon="⚠️" label="Expiring &lt;30 Days" value={expiring30} tone={expiring30 ? "warn" : "success"} />
              <StatCard icon="🔧" label="Open Defects" value={openDefects} tone={openDefects ? "danger" : "success"} />
              <StatCard icon="✅" label="Pending Tasks" value={tasks.filter(t => t.status === "pending").length} tone={tasks.filter(t => t.status === "pending").length ? "warn" : "success"} />
            </div>

            {/* 🎯 TOP 3 URGENT ACTIONS */}
            {topUrgentActions.length > 0 && (
              <Section title="🔥 Top 3 Urgent Actions">
                {topUrgentActions.map(item => (
                  <UrgentActionCard key={item.id} item={item} onActionClick={handleActionClick} />
                ))}
              </Section>
            )}

            <Section title="Recent Vehicles">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <th style={{ textAlign: "left", padding: "12px", fontWeight: 600 }}>Reg</th>
                      <th style={{ textAlign: "left", padding: "12px", fontWeight: 600 }}>Make</th>
                      <th style={{ textAlign: "left", padding: "12px", fontWeight: 600 }}>MOT</th>
                      <th style={{ textAlign: "left", padding: "12px", fontWeight: 600 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.slice(0, 5).map(v => {
                      // ✅ FIX #3: Cache getNextAction call
                      const action = getNextAction(v);
                      const motDays = daysLeft(getVehicleMotDate(v));
                      return (
                        <tr key={v.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "12px", fontWeight: 500 }}>{v.reg}</td>
                          <td style={{ padding: "12px" }}>{v.make}</td>
                          <td style={{ padding: "12px" }}>{fmt(getVehicleMotDate(v))} <span style={{ fontSize: 11, color: "#6b7280" }}>{getDaysLeftText(getVehicleMotDate(v))}</span></td>
                          <td style={{ padding: "12px" }}>
                            <StatusPill days={motDays} />
                            {action && (
                              <button 
                                onClick={() => handleActionClick({
                                  priority: action.days, type: "vehicle", message: `${v.reg}: ${action.action}`,
                                  actionLabel: action.action, vehicle: v, actionType: action.type
                                })} 
                                style={{ ...actionButton(action.days <= 3 ? "critical" : "warn"), marginLeft: 8, padding: "4px 10px", fontSize: 11 }}
                              >
                                {action.action}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}

        {/* 📋 TASKS VIEW */}
        {view === "tasks" && (
          <Section title="✅ My Tasks" action={tasks.length > 0 && (
            <button onClick={() => setTasks(tasks.map(t => t.status === "pending" ? {...t, status: "completed", completedAt: new Date().toISOString()} : t))} 
                    style={{ ...actionButton("neutral"), fontSize: 12 }}>Mark All Complete</button>
          )}>
            {tasks.length === 0 ? (
              <p style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>No tasks yet. Actions you confirm will appear here.</p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {tasks.map(task => (
                  <div key={task.id} style={{ 
                    display: "flex", alignItems: "center", gap: 12, padding: 14, 
                    background: task.status === "completed" ? "#f9fafb" : "#fff", 
                    border: `1px solid ${task.status === "completed" ? "#e5e7eb" : "#d1d5db"}`, 
                    borderRadius: 12 
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, textDecoration: task.status === "completed" ? "line-through" : "none" }}>
                        {task.action} — {task.vehicleReg}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        Due: {fmt(task.dueDate)} {getDaysLeftText(task.dueDate)} • {task.notes || "No notes"}
                      </div>
                    </div>
                    <span style={pill(task.status === "completed" ? "ok" : "warn")}>
                      {task.status === "completed" ? "✓ Done" : "⏳ Pending"}
                    </span>
                    {task.status === "pending" && (
                      <button onClick={() => completeTask(task.id)} 
                              style={{ ...actionButton("primary"), padding: "6px 14px", fontSize: 12 }}>Complete</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* 🔔 ALERTS */}
        {view === "alerts" && (
          <Section title="Notifications">
            {alerts.length === 0 ? (
              <p style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>No alerts</p>
            ) : (
              alerts.map(a => (
                <div key={a.id} style={{ padding: 16, marginBottom: 12, background: a.type === "critical" ? "#fee2e2" : "#fef3c7", borderRadius: 12 }}>
                  <div style={{ fontWeight: 600 }}>{a.message}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{new Date(a.created_at).toLocaleString()}</div>
                </div>
              ))
            )}
          </Section>
        )}

        {/* 🚛 VEHICLES TABLE */}
        {view === "vehicles" && (
          <div style={{ background: "#fff", borderRadius: 16, overflow: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Reg</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Make/Model</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>MOT</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Tax</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Insurance</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Status</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Action</th>
                  <th style={{ padding: "14px 12px", textAlign: "center", fontWeight: 600, fontSize: 13 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.length === 0 ? (
                  <tr><td colSpan="8" style={{ padding: 60, textAlign: "center", color: "#6b7280" }}>No vehicles found.</td></tr>
                ) : (
                  filteredVehicles.map(v => {
                    // ✅ FIX #3: Cache getNextAction
                    const action = getNextAction(v);
                    return (
                      <tr key={v.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "12px", fontWeight: 600 }}>{v.reg}</td>
                        <td style={{ padding: "12px" }}>{v.make} {v.model || ""}</td>
                        <td style={{ padding: "12px" }}><StatusPill days={daysLeft(getVehicleMotDate(v))} /><div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{fmt(getVehicleMotDate(v))}</div></td>
                        <td style={{ padding: "12px" }}><StatusPill days={daysLeft(getVehicleTaxDate(v))} /><div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{fmt(getVehicleTaxDate(v))}</div></td>
                        <td style={{ padding: "12px" }}><StatusPill days={daysLeft(v.ins)} /><div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{fmt(v.ins)}</div></td>
                        <td style={{ padding: "12px" }}>
                          {vehicleStatus(v) === "bad" ? <span style={pill("bad")}>Non-Compliant</span> : vehicleStatus(v) === "warn" ? <span style={pill("warn")}>Action Needed</span> : <span style={pill("ok")}>Compliant</span>}
                        </td>
                        <td style={{ padding: "12px" }}>
                          {action ? (
                            <button onClick={() => handleActionClick({
                              priority: action.days, type: "vehicle", message: `${v.reg}: ${action.action}`,
                              actionLabel: action.action, vehicle: v, actionType: action.type
                            })} style={actionButton(action.days <= 3 ? "critical" : "warn")}>
                              {action.action}
                            </button>
                          ) : <span style={{ fontSize: 12, color: "#6b7280" }}>All good ✓</span>}
                        </td>
                        <td style={{ padding: "12px", textAlign: "center" }}>
                          <button onClick={() => openModal("vehicle", v)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, marginRight: 8 }}>✏️</button>
                          <button onClick={() => delVehicle(v.id, v.reg)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#dc2626" }}>🗑️</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 👤 DRIVERS TABLE */}
        {view === "drivers" && (
          <div style={{ background: "#fff", borderRadius: 16, overflow: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Name</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Licence</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>DQC</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Digital Card</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Status</th>
                  <th style={{ padding: "14px 12px", textAlign: "center", fontWeight: 600, fontSize: 13 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: 60, textAlign: "center", color: "#6b7280" }}>No drivers found.</td></tr>
                ) : (
                  filteredDrivers.map(d => (
                    <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px", fontWeight: 600 }}>{d.name}</td>
                      <td style={{ padding: "12px" }}>{d.lic}</td>
                      <td style={{ padding: "12px" }}><StatusPill days={daysLeft(d.dexp)} /><div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{fmt(d.dexp)}</div></td>
                      <td style={{ padding: "12px" }}><StatusPill days={daysLeft(d.digitalCardExpiry)} /><div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{fmt(d.digitalCardExpiry)}</div></td>
                      <td style={{ padding: "12px" }}>{driverStatus(d) === "bad" ? <span style={pill("bad")}>Expired</span> : driverStatus(d) === "warn" ? <span style={pill("warn")}>Expiring Soon</span> : <span style={pill("ok")}>Valid</span>}</td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button onClick={() => openModal("driver", d)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, marginRight: 8 }}>✏️</button>
                        <button onClick={() => delDriver(d.id, d.name)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#dc2626" }}>🗑️</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 🔧 DEFECTS TABLE */}
        {view === "defects" && (
          <div style={{ background: "#fff", borderRadius: 16, overflow: "auto", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Vehicle</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Category</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Description</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Severity</th>
                  <th style={{ padding: "14px 12px", textAlign: "left", fontWeight: 600, fontSize: 13 }}>Status</th>
                  <th style={{ padding: "14px 12px", textAlign: "center", fontWeight: 600, fontSize: 13 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefects.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: 60, textAlign: "center", color: "#6b7280" }}>No defects found.</td></tr>
                ) : (
                  filteredDefects.map(d => (
                    <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px", fontWeight: 600 }}>{d.reg}</td>
                      <td style={{ padding: "12px" }}>{d.cat}</td>
                      <td style={{ padding: "12px" }}>{d.desc}</td>
                      <td style={{ padding: "12px" }}>{d.sev === "critical" ? <span style={pill("bad")}>Critical</span> : d.sev === "major" ? <span style={pill("warn")}>Major</span> : <span style={pill("mid")}>Minor</span>}</td>
                      <td style={{ padding: "12px" }}>{d.stat === "resolved" ? <span style={pill("ok")}>Resolved</span> : d.stat === "in_progress" ? <span style={pill("warn")}>In Progress</span> : <span style={pill("bad")}>Open</span>}</td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button onClick={() => openModal("defect", d)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, marginRight: 8 }}>✏️</button>
                        <button onClick={() => delDefect(d.id)} disabled={!isAdmin} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#dc2626" }}>🗑️</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modals */}
      {modal === "vehicle" && <Modal title={editData ? `Edit Vehicle - ${editData.reg}` : "Add New Vehicle"} onClose={closeModal}><VehicleForm data={editData} onSave={saveVehicle} onClose={closeModal} /></Modal>}
      {modal === "driver" && <Modal title={editData ? `Edit Driver - ${editData.name}` : "Add New Driver"} onClose={closeModal}><DriverForm data={editData} onSave={saveDriver} onClose={closeModal} vehicles={vehicles} /></Modal>}
      {modal === "defect" && <Modal title={editData ? "Edit Defect" : "Log New Defect"} onClose={closeModal}><DefectForm data={editData} onSave={saveDefect} onClose={closeModal} vehicles={vehicles} drivers={drivers} /></Modal>}
      {modal === "planLimit" && (
        <Modal title="Limit Reached - Upgrade to Transport Manager Pro" onClose={closeModal} size="small">
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
              Your Free plan allows up to 2 vehicles and 2 drivers.
              {planLimitTarget ? ` You already have the maximum number of ${planLimitTarget === "vehicle" ? "vehicles" : "drivers"} allowed on the Free plan.` : ""}
            </div>
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 12, padding: 14, color: "#5b21b6", fontSize: 13 }}>
              Upgrade to <strong>Transport Manager Pro</strong> to add more vehicles and drivers.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={closeModal} style={{ padding: "10px 20px", background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* ✅ FIX #1: Clean single-state modal */}
      {actionModal && (
        <ActionModal 
          actionData={actionModal}
          onClose={() => setActionModal(null)}
          onConfirm={handleConfirmAction}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: toast.type === "warn" ? "#f97316" : "#10b981", color: "#fff", padding: "12px 20px", borderRadius: 10, zIndex: 1100, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
